<template>
	<div class="platform-info-container">
		<!-- 页面头部 -->
		<div class="page-header">
			<div class="header-content">
				<div class="header-left">
					<div class="back-button" @click="back()">
						<i class="el-icon-arrow-left"></i>
					</div>
					<div class="header-info">
						<h1 class="page-title">
							{{ isEdit ? '编辑平台信息' : '新增平台信息' }}
						</h1>
						<div class="breadcrumb">
							<span class="breadcrumb-item">平台信息管理</span>
							<i class="el-icon-arrow-right"></i>
							<span class="breadcrumb-current">
								{{ isEdit ? '编辑信息' : '新增信息' }}
							</span>
						</div>
					</div>
				</div>
				<div class="header-actions">
					<div class="save-status" v-if="saveStatus" :class="saveStatusClass">
						<i :class="saveStatusIcon"></i>
						<span>{{ saveStatusText }}</span>
					</div>
					<el-button @click="back()" class="cancel-btn">
						<i class="el-icon-close"></i>
						取消
					</el-button>
					<el-button type="primary" @click="onSubmit" :loading="submitLoading" class="submit-btn">
						<i class="el-icon-check"></i>
						{{ submitLoading ? '保存中...' : (isEdit ? '更新信息' : '保存信息') }}
					</el-button>
				</div>
			</div>
		</div>

		<!-- 表单内容 -->
		<div class="form-content">
			<el-form
				ref="ruleForm"
				:model="ruleForm"
				:rules="rules"
				label-width="120px"
				class="platform-form"
			>
				<!-- 基础信息 -->
				<div class="form-section">
					<h3 class="section-title">
						<svg class="section-icon" viewBox="0 0 24 24">
							<path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
						</svg>
						基础信息
					</h3>
					
					<!-- 标题 -->
					<el-form-item label="标题" prop="title" class="form-item">
						<div class="input-wrapper">
							<div class="input-field">
								<svg class="field-icon" viewBox="0 0 24 24">
									<path d="M5,4V7H10.5V19H13.5V7H19V4H5Z"/>
								</svg>
								<el-input 
									v-model="ruleForm.title" 
									placeholder="请输入标题"
									maxlength="100"
									show-word-limit
									clearable
									class="form-input"
								></el-input>
							</div>
							<span class="field-tip">
								请输入平台信息的标题，建议在2-100个字符之间
							</span>
						</div>
					</el-form-item>

					<!-- 类型 -->
					<el-form-item label="类型" prop="type" class="form-item">
						<div class="input-wrapper">
							<div class="input-field select-field">
								<svg class="field-icon" viewBox="0 0 24 24">
									<path d="M7,10L12,15L17,10H7Z"/>
								</svg>
								<el-select v-model="ruleForm.type" placeholder="请选择类型" class="form-select">
									<el-option label="平台信息" value="platform">
										<span class="option-content">
											<i class="el-icon-info" style="color: #409EFF;"></i>
											平台信息
										</span>
									</el-option>
									<el-option label="注册协议" value="agreement">
										<span class="option-content">
											<i class="el-icon-document" style="color: #67C23A;"></i>
											注册协议
										</span>
									</el-option>
									<el-option label="隐私政策" value="privacy">
										<span class="option-content">
											<i class="el-icon-lock" style="color: #E6A23C;"></i>
											隐私政策
										</span>
									</el-option>
									<el-option label="服务条款" value="terms">
										<span class="option-content">
											<i class="el-icon-files" style="color: #909399;"></i>
											服务条款
										</span>
									</el-option>
								</el-select>
							</div>
							<span class="field-tip">
								选择平台信息的类型分类
							</span>
						</div>
					</el-form-item>

					<!-- 状态和排序 -->
					<div class="form-row">
						<el-form-item label="状态" prop="status" class="form-item status-item">
							<div class="input-wrapper">
								<div class="status-selector">
									<div 
										v-for="status in [1, 0]" 
										:key="status"
										class="status-option" 
										:class="{ 'active': ruleForm.status === status }"
										@click="ruleForm.status = status"
									>
										<i :class="status === 1 ? 'el-icon-check' : 'el-icon-close'"></i>
										<span>{{ status === 1 ? '启用' : '禁用' }}</span>
									</div>
								</div>
								<span class="field-tip">
									设置信息的显示状态
								</span>
							</div>
						</el-form-item>

						<el-form-item label="排序" prop="sort" class="form-item sort-item">
							<div class="input-wrapper">
								<div class="input-field">
									<svg class="field-icon" viewBox="0 0 24 24">
										<path d="M9,3V4H4V6H9V7L12,4L9,1V2H2V3H9M15,21V20H20V18H15V17L12,20L15,23V22H22V21H15M3,9V19H5V14H7V19H9V9H3M11,9V19H13V16H16V14H13V11H16V9H11Z"/>
									</svg>
									<el-input-number 
										v-model="ruleForm.sort" 
										:min="0" 
										:max="999"
										placeholder="排序值"
										class="form-input"
									></el-input-number>
								</div>
								<span class="field-tip">
									数值越小排序越靠前
								</span>
							</div>
						</el-form-item>
					</div>

					<!-- 摘要 -->
					<el-form-item label="摘要" prop="summary" class="form-item">
						<div class="input-wrapper">
							<div class="input-field textarea-field">
								<svg class="field-icon" viewBox="0 0 24 24">
									<path d="M4,6H20V8H4V6M4,11H20V13H4V11M4,16H14V18H4V16Z"/>
								</svg>
								<el-input 
									v-model="ruleForm.summary" 
									type="textarea"
									:rows="3"
									placeholder="请输入内容摘要（选填）"
									maxlength="200"
									show-word-limit
									clearable
									class="form-textarea"
								></el-input>
							</div>
							<span class="field-tip">
								简要描述平台信息的主要内容，不超过200字符
							</span>
						</div>
					</el-form-item>
				</div>

				<!-- 内容编辑 -->
				<div class="form-section">
					<h3 class="section-title">
						<svg class="section-icon" viewBox="0 0 24 24">
							<path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
						</svg>
						详细内容
					</h3>
					
					<el-form-item label="内容" prop="content" class="form-item content-item">
						<div class="input-wrapper">
							<div class="editor-container">
								<div class="editor-toolbar">
									<div class="toolbar-left">
										<el-button-group size="small" class="format-group">
											<el-button @click="insertText('**粗体文本**')" title="粗体">
												<b>B</b>
											</el-button>
											<el-button @click="insertText('*斜体文本*')" title="斜体">
												<i>I</i>
											</el-button>
											<el-button @click="insertText('# 标题')" title="标题">H</el-button>
											<el-button @click="insertText('- 列表项')" title="列表">列表</el-button>
											<el-button @click="insertText('> 引用内容')" title="引用">引用</el-button>
										</el-button-group>
									</div>
									<div class="toolbar-right">
										<span class="editor-tip">支持Markdown格式</span>
										<span class="word-count">{{ contentWordCount }}/5000</span>
									</div>
								</div>
								<div class="editor-field">
									<el-input 
										ref="contentEditor"
										v-model="ruleForm.content" 
										type="textarea"
										:rows="15"
										placeholder="请输入详细内容..."
										maxlength="5000"
										class="content-editor"
										@input="updateWordCount"
									></el-input>
								</div>
							</div>
							<span class="field-tip">
								支持Markdown语法，可以使用上方工具栏快速插入格式
							</span>
						</div>
					</el-form-item>
				</div>
			</el-form>
		</div>
	</div>
</template>
<script>
export default {
  data() {
    return {
			id: null,
			isEdit: false,
			submitLoading: false,
			ruleForm: {
				title: '',
				type: '',
				status: 1,
				sort: 0,
				summary: '',
				content: ''
			},
      rules: {
				title: [
					{ required: true, message: "标题不能为空", trigger: "blur" },
					{ min: 2, max: 100, message: "标题长度在 2 到 100 个字符", trigger: "blur" }
				],
				type: [
					{ required: true, message: "请选择类型", trigger: "change" }
				],
				content: [
					{ required: true, message: "内容不能为空", trigger: "blur" },
					{ min: 10, message: "内容至少10个字符", trigger: "blur" }
				]
			},
			saveStatus: false,
			saveStatusIcon: '',
			saveStatusText: '',
			saveStatusClass: '',
			contentWordCount: 0
    };
  },
	
  props: ["parent"],
	
	computed: {
		isEditMode() {
			return this.id && this.id !== null;
		},
		saveStatusClass() {
			if (this.saveStatusIcon === 'el-icon-loading') {
				return 'saving';
			} else if (this.saveStatusIcon === 'el-icon-circle-check') {
				return 'success';
			} else if (this.saveStatusIcon === 'el-icon-circle-close') {
				return 'error';
			}
			return '';
		}
	},
	
  methods: {
    // 初始化
    init(id) {
      this.id = id;
			this.isEdit = !!id;
			
			if (this.isEdit) {
				this.loadData();
			} else {
				this.resetForm();
			}
		},

		// 加载数据
		loadData() {
			if (!this.id) return;
			
      this.$http({
				url: `/chat/info/${this.id}`,
				method: "get"
      }).then(({ data }) => {
        if (data && data.code === 0) {
					this.ruleForm = {
						...this.ruleForm,
						...data.data
					};
					this.updateWordCount();
        } else {
					this.$message.error(data.msg || '加载数据失败');
					this.back();
        }
			}).catch((error) => {
				console.error('加载数据失败:', error);
				this.$message.error('加载数据失败');
				this.back();
      });
    },

		// 提交保存
    onSubmit() {
      this.$refs["ruleForm"].validate(valid => {
        if (valid) {
					this.submitLoading = true;
					this.setSaveStatus('saving', 'el-icon-loading', '保存中...');
					
					const url = this.isEdit ? "/chat/update" : "/chat/save";
					const method = "post";
					
          this.$http({
						url: url,
						method: method,
            data: this.ruleForm
          }).then(({ data }) => {
						this.submitLoading = false;
            if (data && data.code === 0) {
							this.setSaveStatus('success', 'el-icon-circle-check', '保存成功');
							this.$message.success(this.isEdit ? '更新成功' : '保存成功');
							setTimeout(() => {
								this.back();
							}, 1500);
            } else {
							this.setSaveStatus('error', 'el-icon-circle-close', '保存失败');
							this.$message.error(data.msg || '操作失败');
            }
					}).catch((error) => {
						this.submitLoading = false;
						this.setSaveStatus('error', 'el-icon-circle-close', '保存失败');
						console.error('操作失败:', error);
						this.$message.error('操作失败');
          });
        }
      });
    },

		// 设置保存状态
		setSaveStatus(status, icon, text) {
			this.saveStatus = true;
			this.saveStatusIcon = icon;
			this.saveStatusText = text;
			
			if (status !== 'saving') {
				setTimeout(() => {
					this.saveStatus = false;
				}, 3000);
			}
		},

		// 重置表单
		resetForm() {
			this.ruleForm = {
				title: '',
				type: '',
				status: 1,
				sort: 0,
				summary: '',
				content: ''
			};
			this.contentWordCount = 0;
			
			if (this.$refs.ruleForm) {
				this.$refs.ruleForm.clearValidate();
			}
		},

		// 插入文本（Markdown工具）
		insertText(text) {
			const textarea = this.$refs.contentEditor.$refs.textarea;
			const start = textarea.selectionStart;
			const end = textarea.selectionEnd;
			const content = this.ruleForm.content;
			
			this.ruleForm.content = content.substring(0, start) + text + content.substring(end);
			
			this.$nextTick(() => {
				textarea.focus();
				textarea.setSelectionRange(start + text.length, start + text.length);
				this.updateWordCount();
			});
		},

		// 更新字数统计
		updateWordCount() {
			this.contentWordCount = this.ruleForm.content ? this.ruleForm.content.length : 0;
		},

		// 返回列表
    back() {
      this.parent.showFlag = false;
      this.parent.getDataList();
    }
  }
};
</script>
<style lang="scss" scoped>
.platform-info-container {
	background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
	min-height: 100vh;
}

// 页面头部
.page-header {
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	color: white;
	padding: 20px 0;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
	top: 0;
	z-index: 1000;

	.header-content {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 24px;
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 20px;
	}

	.header-left {
		display: flex;
		align-items: center;
		gap: 20px;

		.back-button {
			width: 40px;
			height: 40px;
			border-radius: 50%;
			background: rgba(255, 255, 255, 0.2);
			display: flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			transition: all 0.3s ease;
			
			&:hover {
				background: rgba(255, 255, 255, 0.3);
				transform: scale(1.05);
			}

			i {
				font-size: 18px;
				color: white;
			}
		}

		.header-info {
			.page-title {
				font-size: 24px;
				font-weight: 700;
				color: white;
				margin: 0 0 4px 0;
				text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
			}

			.breadcrumb {
				display: flex;
				align-items: center;
				gap: 8px;
				color: rgba(255, 255, 255, 0.8);
				font-size: 14px;

				.el-icon-arrow-right {
					font-size: 12px;
					color: rgba(255, 255, 255, 0.6);
				}

				.breadcrumb-item {
					color: rgba(255, 255, 255, 0.8);
				}

				.breadcrumb-current {
					color: white;
					font-weight: 500;
				}
			}
		}
	}

	.header-actions {
		display: flex;
		align-items: center;
		gap: 16px;

		.save-status {
			display: flex;
			align-items: center;
			gap: 8px;
			padding: 8px 16px;
			border-radius: 20px;
			background: rgba(255, 255, 255, 0.15);
			backdrop-filter: blur(10px);
			font-size: 14px;
			transition: all 0.3s ease;

			i {
				font-size: 16px;
			}
			
			&.saving {
				background: rgba(64, 158, 255, 0.2);
				color: #409EFF;
			}
			
			&.success {
				background: rgba(103, 194, 58, 0.2);
				color: #67C23A;
			}
			
			&.error {
				background: rgba(245, 108, 108, 0.2);
				color: #F56C6C;
			}
		}

		.el-button {
			padding: 10px 24px;
			font-weight: 500;
			border-radius: 25px;
			font-size: 14px;
			transition: all 0.3s ease;

			i {
				margin-right: 6px;
			}

			&.submit-btn {
				background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
				border: none;
				color: white;
				box-shadow: 0 4px 15px rgba(79, 172, 254, 0.4);

				&:hover {
					transform: translateY(-2px);
					box-shadow: 0 6px 20px rgba(79, 172, 254, 0.6);
				}
			}

			&.cancel-btn {
				background: rgba(255, 255, 255, 0.15);
				border: 1px solid rgba(255, 255, 255, 0.3);
				color: white;
				backdrop-filter: blur(10px);

				&:hover {
					background: rgba(255, 255, 255, 0.25);
					transform: translateY(-1px);
				}
			}
		}
	}
}

// 表单内容
.form-content {
	max-width: 1000px;
	margin: 0 auto;
	padding: 30px 24px;
}

// 表单样式
.platform-form {
	background: white;
	padding: 40px;
	border-radius: 16px;
	box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
	backdrop-filter: blur(10px);

	// 表单分组
	.form-section {
		margin-bottom: 40px;

		&:last-of-type {
			margin-bottom: 0;
		}

		.section-title {
			font-size: 20px;
			font-weight: 700;
			color: #2d3748;
			margin: 0 0 30px 0;
			padding-bottom: 12px;
			border-bottom: 3px solid #e2e8f0;
			display: flex;
			align-items: center;
			gap: 12px;
			position: relative;

			&::after {
				content: '';
				position: absolute;
				bottom: -3px;
				left: 0;
				width: 60px;
				height: 3px;
				background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
				border-radius: 2px;
			}

			.section-icon {
				width: 28px;
				height: 28px;
				fill: #667eea;
			}
		}
	}

	// 表单项样式
	:deep(.el-form-item) {
		margin-bottom: 30px;

		.el-form-item__label {
			font-weight: 600;
			color: #374151;
			line-height: 32px;
			font-size: 15px;
		}
	}

	// 输入包装器
	.input-wrapper {
		.input-field {
			position: relative;
			border: 2px solid #e5e7eb;
			border-radius: 12px;
			background: #fafafa;
			transition: all 0.3s ease;
			overflow: hidden;

			&:hover:not(.readonly) {
				border-color: #c3d4f0;
				background: white;
			}

			&:focus-within:not(.readonly) {
				border-color: #667eea;
				background: white;
				box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
			}

			&.select-field {
				padding: 0;
			}

			&.textarea-field {
				padding: 16px;
			}

			.field-icon {
				position: absolute;
				left: 16px;
				top: 50%;
				transform: translateY(-50%);
				width: 20px;
				height: 20px;
				fill: #9ca3af;
				z-index: 2;
				transition: all 0.3s ease;
			}

			&:focus-within .field-icon {
				fill: #667eea;
			}

			.form-input {
				:deep(.el-input__inner) {
					border: none;
					padding: 14px 16px 14px 50px;
					font-size: 15px;
					background: transparent;
					color: #374151;

					&::placeholder {
						color: #9ca3af;
					}

					&:focus {
						border: none;
						box-shadow: none;
					}
				}
			}

			.form-select {
				width: 100%;

				:deep(.el-input__inner) {
					border: none;
					padding: 14px 16px 14px 50px;
					font-size: 15px;
					background: transparent;
					color: #374151;

					&:focus {
						border: none;
						box-shadow: none;
					}
				}

				:deep(.el-input__suffix) {
					right: 16px;
				}
			}

			.form-textarea {
				:deep(.el-textarea__inner) {
					border: none;
					padding: 0 0 0 34px;
					font-size: 15px;
					background: transparent;
					color: #374151;
					line-height: 1.6;
					resize: vertical;

					&::placeholder {
						color: #9ca3af;
					}

					&:focus {
						border: none;
						box-shadow: none;
					}
				}
			}
		}

		.field-tip {
			color: #6b7280;
			font-size: 13px;
			margin-top: 8px;
			display: block;
			line-height: 1.4;
		}
	}

	// 选项内容样式
	.option-content {
		display: flex;
		align-items: center;
		gap: 8px;

		i {
			font-size: 16px;
		}
	}

	// 表单行
	.form-row {
		display: flex;
		gap: 30px;
		margin-top: 30px;

		.el-form-item {
			flex: 1;
		}
	}

	// 状态选择器
	.status-selector {
		display: flex;
		gap: 12px;

		.status-option {
			padding: 12px 20px;
			border: 2px solid #e5e7eb;
			border-radius: 12px;
			background: #fafafa;
			cursor: pointer;
			transition: all 0.3s ease;
			display: flex;
			align-items: center;
			gap: 8px;
			font-weight: 500;
			min-width: 100px;
			justify-content: center;

			&:hover {
				border-color: #c3d4f0;
				background: white;
			}

			&.active {
				border-color: #667eea;
				background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
				color: white;
				box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
			}

			i {
				font-size: 16px;
			}

			span {
				font-size: 14px;
			}
		}
	}

	// 编辑器容器
	.editor-container {
		border: 2px solid #e5e7eb;
		border-radius: 12px;
		background: #fafafa;
		transition: all 0.3s ease;
		overflow: hidden;

		&:focus-within {
			border-color: #667eea;
			background: white;
			box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
		}

		.editor-toolbar {
			background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
			padding: 16px 20px;
			border-bottom: 1px solid #e5e7eb;
			display: flex;
			justify-content: space-between;
			align-items: center;

			.toolbar-left {
				display: flex;
				gap: 12px;
			}

			.toolbar-right {
				display: flex;
				align-items: center;
				gap: 16px;
			}

			.format-group {
				:deep(.el-button) {
					border: 1px solid #d1d5db;
					font-size: 13px;
					padding: 6px 12px;
					border-radius: 8px;
					background: white;
					color: #374151;
					font-weight: 500;

					&:hover {
						color: #667eea;
						border-color: #667eea;
						background: #f0f4ff;
					}

					&:first-child {
						border-top-right-radius: 0;
						border-bottom-right-radius: 0;
						border-right: none;
					}

					&:last-child {
						border-top-left-radius: 0;
						border-bottom-left-radius: 0;
					}

					&:not(:first-child):not(:last-child) {
						border-radius: 0;
						border-right: none;
					}
				}
			}

			.editor-tip {
				color: #6b7280;
				font-size: 13px;
				font-weight: 500;
			}

			.word-count {
				color: #9ca3af;
				font-size: 13px;
				font-weight: 600;
				padding: 4px 12px;
				background: white;
				border-radius: 20px;
				border: 1px solid #e5e7eb;
			}
		}

		.editor-field {
			.content-editor {
				:deep(.el-textarea__inner) {
					border: none;
					border-radius: 0;
					padding: 20px;
					font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
					font-size: 14px;
					line-height: 1.6;
					background: transparent;
					color: #374151;
					
					&:focus {
						border: none;
						box-shadow: none;
					}

					&::placeholder {
						color: #9ca3af;
					}
				}
			}
		}
	}
}

// 响应式设计
@media (max-width: 1200px) {
	.form-content {
		max-width: 800px;
		padding: 24px 20px;
	}

	.platform-form {
		padding: 30px;
	}
}

@media (max-width: 768px) {
	.page-header {
		padding: 16px 0;

		.header-content {
			flex-direction: column;
			align-items: stretch;
			gap: 16px;
			padding: 0 16px;
		}

		.header-left {
			gap: 16px;

			.header-info .page-title {
				font-size: 20px;
			}

			.breadcrumb {
				font-size: 13px;
			}
		}

		.header-actions {
			justify-content: center;
			
			.save-status {
				font-size: 13px;
				padding: 6px 12px;
			}

			.el-button {
				font-size: 13px;
				padding: 8px 16px;
			}
		}
	}

	.form-content {
		padding: 20px 16px;
	}

	.platform-form {
		padding: 24px;

		.form-section {
			margin-bottom: 30px;

			.section-title {
				font-size: 18px;
				margin-bottom: 20px;
			}
		}

		:deep(.el-form-item) {
			margin-bottom: 24px;

			.el-form-item__label {
				font-size: 14px;
				line-height: 28px;
			}
		}

		.form-row {
			flex-direction: column;
			gap: 20px;
		}

		.status-selector {
			flex-direction: column;
			gap: 8px;

			.status-option {
				min-width: auto;
			}
		}

		.editor-container {
			.editor-toolbar {
				padding: 12px 16px;
				flex-direction: column;
				gap: 12px;
				align-items: stretch;

				.toolbar-left {
					justify-content: center;
				}

				.toolbar-right {
					flex-direction: column;
					gap: 8px;
					align-items: center;
				}
			}
		}
	}
}

@media (max-width: 480px) {
	.page-header {
		.header-left {
			.header-info .breadcrumb {
				display: none;
			}
		}
	}

	.platform-form {
		padding: 20px;

		.form-section {
			.section-title {
				font-size: 16px;
			}
		}

		:deep(.el-form-item) {
			.el-form-item__label {
				font-size: 13px;
			}
		}

		.input-wrapper {
			.input-field {
				.field-icon {
					width: 18px;
					height: 18px;
					left: 12px;
				}

				.form-input :deep(.el-input__inner) {
					padding: 12px 12px 12px 40px;
					font-size: 14px;
				}

				.form-select :deep(.el-input__inner) {
					padding: 12px 12px 12px 40px;
					font-size: 14px;
				}

				&.textarea-field {
					padding: 12px;

					.form-textarea :deep(.el-textarea__inner) {
						padding: 0 0 0 28px;
						font-size: 14px;
					}
				}
			}
		}

		.editor-container {
			.editor-toolbar {
				.format-group :deep(.el-button) {
					font-size: 12px;
					padding: 4px 8px;
				}
			}

			.editor-field {
				.content-editor :deep(.el-textarea__inner) {
					padding: 16px;
					font-size: 13px;
				}
			}
		}
	}
}
</style>
